import { useMutation, useQuery } from '@tanstack/react-query'
import { api, apiVisitEngine } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/query-keys'
import type { IApiResponse } from '@/shared/types/api'
import type {
  IDonorCardDetail,
  IDonorCardHistory,
  IDonorDetail,
  IPrefilledDonor,
} from '../model/donor-detail.types'
import { toaster } from '@/shared/ui/toaster'
import type { AxiosError } from 'axios'

// ── Raw API ───────────────────────────────────────────────────────────────────

export const donorDetailApi = {
  getCardDetailById: async (person_uuid: string) => {
    const response = await apiVisitEngine.get<IApiResponse<IDonorCardDetail>>(
      `/v1/therapist/donor-card/${person_uuid}`,
    )
    return response.data.data
  },
  getFullDonorProfileById: async (visitId: string) => {
    const response = await apiVisitEngine.get<IApiResponse<IDonorDetail>>(
      `/v1/donors/${visitId}`,
    )
    return response.data.data
  },

  getCardHistory: async (donorId: string) => {
    const response = await apiVisitEngine.get<
      IApiResponse<IDonorCardHistory[]>
    >(`/v1/donors/${donorId}/card-history`)
    return response.data.data
  },

  getDonorPreCheck: async (pinfl: string) => {
    const response = await api.post(`/api/v1/donors/pre-check`, { pinfl })
    return response.data
  },
  getPrefilledDonor: async (pinfl: string) => {
    const response = await apiVisitEngine.post<IApiResponse<IPrefilledDonor>>(
      `/v1/visits/prefill`,
      {
        pinfl,
      },
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data.data
  },
}

// ── Query hooks ───────────────────────────────────────────────────────────────

export const useGetDonorCardDetailQuery = (person_uuid: string) =>
  useQuery({
    queryKey: queryKeys.donorCard.detail(person_uuid),
    queryFn: () => donorDetailApi.getCardDetailById(person_uuid),
    retry: false,
    enabled: !!person_uuid,
  })

export const useGetDonorDetailQuery = (visitId: string) =>
  useQuery({
    queryKey: queryKeys.donors.detail(visitId),
    queryFn: () => donorDetailApi.getFullDonorProfileById(visitId),
    retry: false,
    enabled: !!visitId,
  })

export const useGetDonorCardHistoryQuery = (donorId: string) =>
  useQuery({
    queryKey: queryKeys.donors.cardHistory(donorId),
    queryFn: () => donorDetailApi.getCardHistory(donorId),
    retry: false,
    enabled: !!donorId,
  })

export const useGetPrefilledDonorMutation = () => {
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (data: { pinfl: string }) =>
      donorDetailApi.getPrefilledDonor(data.pinfl),
    onError: (error: AxiosError) => {
      switch (error.status) {
        case 400:
          toaster.error({
            title: t('donor.registration.validation.mip_not_found'),
          })
          break
        case 503:
          toaster.error({
            title: t('donor.registration.validation.mip_not_available'),
          })
          break
      }
    },
  })
}
